import i18n from '@/utils/i18n';
import React, { useCallback, useMemo } from 'react';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { createStyles } from '@/contexts/theme-context';
import { MetricCard } from '@usepepper/sprout-kit-ui';
import { navigateToSalesPortalRoute } from '@/experiences/sales-portal/sales-portal-route-config';
import { PRICING_IMPACT_MICRO_PRODUCT_ID } from '@sales-portal/domain/pricing/constants';
import { PricingBreadcrumbs } from '@sales-portal/domain/pricing/components/pricing-breadcrumbs';
import { PricingHubRecentChangeRow } from '@sales-portal/domain/pricing/screens/overview/types';
import { PricingOverviewBlockCard } from '@sales-portal/domain/pricing/screens/overview/components/pricing-overview-block-card';
import { PricingPendingEditsBanner } from '@sales-portal/domain/pricing/screens/overview/components/pricing-pending-edits-banner';
import { PricingRecentPriceChangesTable } from '@sales-portal/domain/pricing/screens/overview/components/pricing-recent-price-changes-table';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalPricingStackParams } from '@/navigators/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useNavigation } from '@react-navigation/native';
import { usePricingEditsStore } from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store';
import { usePricingEditsTimezoneSync } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-pricing-edits-timezone-sync';
import { usePricingMicroProducts } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-micro-products';
import { usePricingOverviewData } from '@sales-portal/domain/pricing/screens/overview/blocs/hooks/use-pricing-overview-data';
import { View } from 'react-native';

// Cell floor driving the group's columns: 4 blocks go 4-up at >= 880,
// 2x2 at >= 440, stacked below.
const BLOCK_CARD_MIN_CELL_WIDTH = 220;

export const PricingOverviewScreen: React.FC = () => {
  const [styles] = useStyles();
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalPricingStackParams>>();

  const {
    config,
    errors,
    loading,
    metrics,
    recentChanges,
    recentChangesError,
    retryRecentChanges,
  } = usePricingOverviewData();
  const { currencyCode } = usePricingEditsTimezoneSync();

  // Primitive selectors, so the reduce can't churn the store subscription.
  const editCount = usePricingEditsStore((state) => state.edits.length);
  const weeklyEditsImpactInMicros = usePricingEditsStore((state) =>
    state.edits.reduce(
      (total, edit) => total + (edit.grossProfitImpactInMicros || 0),
      0,
    ),
  );

  const { loading: microProductsLoading, products: microProducts } =
    usePricingMicroProducts();
  const hasImpactMicroProduct =
    !microProductsLoading &&
    microProducts.some(
      (product) => product.id === PRICING_IMPACT_MICRO_PRODUCT_ID,
    );

  const goTo = useCallback(
    (route: SALES_PORTAL_ROUTES) => () =>
      navigateToSalesPortalRoute(navigation, route),
    [navigation],
  );

  // Deep-links into pricing_impact's line detail, anchored on the change's day.
  // The hash grammar is the microproduct's (routeCodec) — kept verbatim here.
  const goToImpactLine = (row: PricingHubRecentChangeRow) =>
    navigateToSalesPortalRoute(
      navigation,
      SALES_PORTAL_ROUTES.PricingMicroProduct,
      {
        mpRoute: `/lines/${row.customerUuid}/${row.variantPackUuid}/from/${row.eventDate}`,
        productId: PRICING_IMPACT_MICRO_PRODUCT_ID,
      },
    );

  // MetricCard.Group derives its columns from the child count, and
  // Children.count counts null slots — so hidden blocks are filtered out.
  const blockCards = useMemo(
    () =>
      [
        config.showAgentRecs ? (
          <PricingOverviewBlockCard
            hasError={errors.agentRecs}
            isLoading={loading.agentRecs}
            key="agent-recs"
            metric={metrics.agentRecs}
            onPress={goTo(SALES_PORTAL_ROUTES.PricingAgentTasks)}
            title={i18n.t('pricing.overview.agent_recs.title')}
            withCaption
          />
        ) : null,

        <PricingOverviewBlockCard
          hasError={errors.costChanges}
          isLoading={loading.costChanges}
          key="cost-changes"
          metric={metrics.costChanges}
          onPress={goTo(SALES_PORTAL_ROUTES.PricingManagement)}
          title={i18n.t('pricing.overview.cost_changes.title')}
          withCaption
        />,

        config.showCustomers ? (
          <PricingOverviewBlockCard
            hasError={errors.customers}
            isLoading={loading.customers}
            key="customers"
            metric={metrics.customers}
            onPress={goTo(SALES_PORTAL_ROUTES.PricingCustomers)}
            title={i18n.t('pricing.overview.customers.title')}
          />
        ) : null,

        config.showBookmarked ? (
          <PricingOverviewBlockCard
            key="bookmarked"
            metric={metrics.bookmarked}
            onPress={goTo(SALES_PORTAL_ROUTES.PricingBookmarked)}
            title={i18n.t('pricing.overview.bookmarked.title')}
          />
        ) : null,
      ]
        .filter((card) => card !== null)
        .map((card) => (
          <View key={card.key} style={styles.blockCell}>
            {card}
          </View>
        )),
    [
      config.showAgentRecs,
      config.showBookmarked,
      config.showCustomers,
      errors.agentRecs,
      errors.costChanges,
      errors.customers,
      goTo,
      loading.agentRecs,
      loading.costChanges,
      loading.customers,
      metrics.agentRecs,
      metrics.bookmarked,
      metrics.costChanges,
      metrics.customers,
      styles.blockCell,
    ],
  );

  return (
    <SalesHubShellTemplate>
      <AppPageLayout leadingActions={<PricingBreadcrumbs />} scroll>
        <View style={styles.container}>
          <Typography variant="heading-lg">
            {i18n.t('pricing.overview.title')}
          </Typography>

          {editCount > 0 ? (
            <PricingPendingEditsBanner
              currencyCode={currencyCode}
              editCount={editCount}
              onPress={() =>
                navigation.push(SALES_PORTAL_ROUTES.PricingManagementReview)
              }
              weeklyImpactInMicros={weeklyEditsImpactInMicros}
            />
          ) : null}

          <View style={styles.blockGrid}>
            <MetricCard.Group gap={0} minCellWidth={BLOCK_CARD_MIN_CELL_WIDTH}>
              {blockCards}
            </MetricCard.Group>
          </View>

          <PricingRecentPriceChangesTable
            hasError={Boolean(recentChangesError)}
            isLoading={loading.recentChanges}
            onRetry={retryRecentChanges}
            onRowPress={hasImpactMicroProduct ? goToImpactLine : undefined}
            onViewAll={
              hasImpactMicroProduct
                ? () =>
                    navigateToSalesPortalRoute(
                      navigation,
                      SALES_PORTAL_ROUTES.PricingMicroProduct,
                      { productId: PRICING_IMPACT_MICRO_PRODUCT_ID },
                    )
                : undefined
            }
            rows={recentChanges}
          />
        </View>
      </AppPageLayout>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  // Half-gutter per cell, cancelled at the grid's outer edge, so the cards
  // align with the page padding. The group's cells are exact percentages of
  // the row, so its own `gap` would push the last card past the container.
  blockCell: {
    flex: 1,
    padding: theme.sk.spacing.spaceSmall,
  },
  blockGrid: {
    margin: -theme.sk.spacing.spaceSmall,
  },
  container: {
    gap: theme.sk.spacing.spaceLarge,
    padding: theme.sk.spacing.spaceLarge,
  },
}));
